import {initSectionsWithIntersection} from '@scripts/helpers/block.js';

export const numberWithSpace = (x) => {
  return x.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ' ');
};

export const initializeSection = async (section) => {
  const sectionItems = Array.from(section.querySelectorAll('[data-gsap-element]'));

  if (!sectionItems.length) {
    return;
  }

  const {gsap} = await import('gsap');
  const {ScrollTrigger} = await import('gsap/ScrollTrigger');
  const {SplitText} = await import('gsap/SplitText');

  gsap.registerPlugin(ScrollTrigger, SplitText);

  const tl = gsap.timeline({
    once: true,
  });

  tl.pause();

  tl.to(section, {
    duration: 0.65,
  });

  ScrollTrigger.create({
    start: 'top 85%',
    trigger: section,
    once: true,
    onEnter: () => {
      tl.play();
    },
    onEnterBack: () => {
      tl.play();
    },
  });

  const batchBoxes = Array.from(section.querySelectorAll('[data-gsap-element="batch-box"]'));

  if (batchBoxes.length) {
    gsap.set(batchBoxes, {
      opacity: 0,
      y: '4rem',
    });

    ScrollTrigger.batch(batchBoxes, {
      start: 'top 80%',
      onEnter: (batch) => {
        gsap.to(batch, {
          opacity: 1,
          y: 0,
          duration: 0.8,
          ease: 'power3.out',
          stagger: 0.2,
        });
      },
    });
  }

  sectionItems.forEach((item) => {
    const animationType = item.getAttribute('data-gsap-element');
    const animationDelay = item.getAttribute('data-gsap-delay') ?? '-=0.65';

    if (animationType === 'overline') {
      gsap.set(item, {opacity: 0, y: '2.4rem'});
      tl.to(
        item,
        {
          opacity: 1,
          y: 0,
          duration: 0.8,
          ease: 'power3.out',
        },
        animationDelay,
      );
    } else if (animationType === 'heading') {
      gsap.set(item, {
        opacity: 0,
        y: '4rem',
      });
      tl.to(
        item,
        {
          opacity: 1,
          y: 0,
          duration: 0.8,
          ease: 'power3.out',
        },
        animationDelay,
      );
    } else if (animationType === 'heading-words') {
      const splitText = new SplitText(item, {type: 'words', tag: 'span', wordsClass: 'word'});

      gsap.set(splitText.words, {
        opacity: 0,
        y: '0.75em',
      });

      tl.to(
        splitText.words,
        {
          opacity: 1,
          y: 0,
          duration: 0.6,
          ease: 'power3.out',
          stagger: 0.075,
        },
        animationDelay,
      );
    } else if (animationType === 'text') {
      gsap.set(item, {opacity: 0, y: '4rem'});
      tl.to(
        item,
        {
          opacity: 1,
          y: 0,
          duration: 0.8,
          ease: 'power3.out',
        },
        animationDelay,
      );
    } else if (animationType === 'button') {
      gsap.set(item, {opacity: 0, y: '4rem'});
      tl.to(
        item,
        {
          opacity: 1,
          y: 0,
          duration: 0.8,
          ease: 'power3.out',
        },
        animationDelay,
      );
    } else if (animationType === 'box') {
      gsap.set(item, {opacity: 0, y: '4rem'});
      tl.to(
        item,
        {
          opacity: 1,
          y: 0,
          duration: 0.8,
          ease: 'power3.out',
        },
        animationDelay,
      );
    } else if (animationType === 'box-wiggle') {
      gsap.set(item, {opacity: 0, y: '4rem'});

      const tlWiggle = gsap.timeline({
        paused: true,
        repeat: 1,
      });

      tlWiggle.to(item, {
        duration: 0.5,
        y: -24,
        ease: 'Power4.inOut',
      });
      tlWiggle.to(item, {
        duration: 0.5,
        y: 0,
        ease: 'Power4.out',
      });

      tl.to(
        item,
        {
          opacity: 1,
          y: 0,
          duration: 0.8,
          ease: 'power3.out',
          onComplete: () => {
            setTimeout(() => {
              tlWiggle.play();
            }, 100);
          },
        },
        animationDelay,
      );
    } else if (animationType === 'image') {
      gsap.set(item, {opacity: 0, y: '4rem'});
      tl.to(
        item,
        {
          opacity: 1,
          y: 0,
          duration: 0.8,
          ease: 'power3.out',
          onComplete: () => {
            item.style.transform = 'none';
          },
        },
        animationDelay,
      );
    } else if (animationType === 'image-clip-reveal') {
      const images = item.querySelectorAll('img:not(.prevent-clip-reveal)');
      const box = item.querySelectorAll('.image-clip-reveal-box');

      gsap.set(item, {
        '--clip-progress': '0%',
      });

      gsap.set(images, {
        scale: 1.15,
        transformOrigin: 'left center',
      });

      if (box) {
        gsap.set(box, {
          scale: 0.9,
          opacity: 0,
        });
      }

      tl.to(
        item,
        {
          duration: 1,
          ease: 'power3.inOut',
          '--clip-progress': '100%',
          onStart: () => {
            gsap.to(images, {
              duration: 1.4,
              ease: 'power3.inOut',
              scale: 1,
            });

            if (box) {
              gsap.to(box, {
                opacity: 1,
                scale: 1,
                duration: 0.8,
                ease: 'power3.out',
                delay: 0.4,
              });
            }
          },
        },
        animationDelay,
      );
    } else if (animationType === 'image-scale') {
      gsap.set(item, {opacity: 0, scale: 0.9});
      tl.to(
        item,
        {
          opacity: 1,
          scale: 1,
          duration: 0.8,
          ease: 'power3.out',
          onComplete: () => {
            item.style.transform = 'none';
          },
        },
        animationDelay,
      );
    } else if (animationType === 'batch-box') {
      // do nothing
    } else if (animationType === 'counter') {
      item.innerHTML = item.innerHTML.replace(/(\d+|\d+\.\d+)/gi, '<span class="counter">$1</span>');
      const itemNumbers = item.querySelector('.counter') || item;
      const value = itemNumbers.textContent;

      console.log(value);

      gsap.set(itemNumbers, {textContent: 0});

      tl.to(
        itemNumbers,
        {
          duration: 0.01,
          onStart: () => {
            gsap.to(itemNumbers, {
              duration: 2,
              ease: 'power3.out',
              snap: {textContent: 1},
              textContent: value,
              stagger: {
                each: 1.0,
                onUpdate: function () {
                  this.targets()[0].textContent = numberWithSpace(Math.ceil(this.targets()[0].textContent));
                },
              },
            });
          },
        },
        animationDelay,
      );
    } else {
      gsap.set(item, {opacity: 0});
      tl.to(
        item,
        {
          opacity: 1,
          duration: 0.8,
          ease: 'power3.out',
        },
        animationDelay,
      );
    }
  });
};

export const initBasicAnimations = () => {
  initSectionsWithIntersection(async () => {
    return Array.from(document.querySelectorAll('[data-gsap-anim="section"]'));
  }, initializeSection);
};
